import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Observable, BehaviorSubject, Subscription, combineLatest, from, of, throwError } from 'rxjs';
import { debounceTime, distinctUntilChanged, exhaustMap, filter, first, map, pairwise, shareReplay, startWith, switchMap, takeUntil, tap, timeout } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  AHdeviceTypeEnum,
  GetCommunicationChannelForStickQuery,
  Operation,
  SendInvUpgradeForWebMutation,
  SendInvUpgradeByAHForWebMutation,
  System,
  GetLoggerDataQuery,
  InsertDeviceUpdateLogMutation,
  InsertLoggerUpdateLogMutation,
  SolarmanDataType,
  Channel,
  SendInvUpgradeByEthPollingForWebMutation,
  OnReceiveAckSubscription,
  ReceiveAckResponse,
  generateTransactionId,
} from 'ngx-core-services';
import {
  CallPROSolarmanFirmwareDetailsResponse,
  CallPROSolarmanDeviceDetailsResponse,
  CallPROSolarmanLastActionResponse,
  InverterUpgradeConfig,
  FirmwareComparison,
  ChipData,
  CommunicationChannelType,
  SofarPackageData,
  SolarmanStickUpgradeConfig,
  compareFirmwareVersions,
  compareInverterFWModuleVersions,
  fwVersionLetter,
} from 'ngx-modbus';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AzzurroTable,
  ColumnConfig,
  ColumnTypeEnum,
  DialogComponent,
  TitleContentComponent,
  ToastFacade,
  UiPromiseService,
} from 'ngx-zcs-components';
import { IonicModule, PopoverController } from '@ionic/angular';
import gql from 'graphql-tag';
import * as Sentry from "@sentry/angular";

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { AccessSupportService } from '../../communication/access-support.service';
import { SystemsState } from '../../store';
import { selectedSystemInvertersWithLoggerData, selectSolarmanUpgradePushState, SolarmanUpgradePushState } from '../../store/selectors/system.selectors';
import { SolarmanRateLimitError, UpgradeService } from '../../communication';
import { computeCRC32, isLoggerUpgrading$ } from './upgrade-inverter.utils';
import { queryCommunicationChannelForStick, queryInvUpgradeForWeb, queryLoggerData } from './upgrade-inverter.query';
import { DeviceDetailModule } from '../device-detail/device-details.module';
import { UpgradeSafetyModule } from '../upgrade-safety/upgrade-safety.module';
import {
  InverterStatusFacade,
  isUpgradeAvailable,
  extractLoggerVersion,
  extractLoggerSn,
  extractDeviceSn,
  extractModuleVersionsFromZCS,
  parseOnlineStatus,
} from '../shared/inverter-status';
import { createDebugLogger, DebugConfig } from '../shared/debug';
import { UPGRADE_COMMON_LABELS } from '../shared/labels';

import { OneClickPanelComponent } from './one-click/one-click-panel.component';
import { ONE_CLICK_LABELS } from './one-click/one-click.labels';
import { OneClickUpgradeService } from './one-click/one-click-upgrade.service';
import { OneClickCurrentVersions, OneClickGate } from './one-click/one-click.utils';

const DEBUG_CONFIG: DebugConfig = { ENABLED: false, LOG_API: true, LOG_STATE: true, LOG_DATA: true };
const Logger = createDebugLogger('[UpgradeInverter]', DEBUG_CONFIG);

const LOGGER_MODULE = 'LOGGER' as const;
const GEN_OPTIMISTIC_UPDATE_KEY = (deviceId: string) => `optimisticUpdateTime_${deviceId.toLowerCase()}`;
const OPTIMISTIC_UPGRADE_DELAY = 5 * 60 * 1000;
const AH_ACK_TIMEOUT_MS = 80000;

const receiveAckResponseQuery = gql`
  fragment receiveackresponse on ReceiveAckResponse {
    transactionId
    version
    deviceId
    timestamp
    dataType
    message
    success
  }
`;
const BASE_MODULES_CONFIG = [
  { module: 'DSPM' as const, solarmanSerialNumber: 2700, solarmanFileType: 1 },
  { module: 'DSPS' as const, solarmanSerialNumber: 2800, solarmanFileType: 2 },
  { module: 'ARM' as const, solarmanSerialNumber: 8000, solarmanFileType: 0 },
];

/**
 *  @Important For now, the BMS, PCU and BDU modules are not included until further checks are made
 * const BATTERY_MODULES_CONFIG = [
    { module: 'BMS' as const, solarmanSerialNumber: 400, solarmanFileType: 4 },
    { module: 'PCU' as const, solarmanSerialNumber: 14800, solarmanFileType: 5 },
    { module: 'BDU' as const, solarmanSerialNumber: 15900, solarmanFileType: 6 },
  ];

  const MODULES = [...BASE_MODULES_CONFIG, ...BATTERY_MODULES_CONFIG];
 */
const MODULES = [...BASE_MODULES_CONFIG];

const getLabels = () => ({
  ...UPGRADE_COMMON_LABELS,
  ...ONE_CLICK_LABELS,
  confirmMessage: 'Do you want to update the module {module} from {currentVersion} to {latestVersion}?',
  confirmBatchMessage: 'Do you want to update the following modules?\n\n{moduleList}',
  deviceType: 'Device type',
  evCharge: 'Ev-charge',
  hybridInverter: 'Hybrid inverter',
  inverter: 'Inverter',
  loadSafetyButton: 'Load safety configuration',
  localeCode: 'en-EN',
  noUpdates: 'No updates available.',
  phase: 'Phase',
  photovoltaicInverter: 'Photovoltaic inverter',
  ratedPower: 'Power',
  refresh: 'Refresh status',
  safetyTitle: 'Upgrade safety',
  serialNumber: 'Serial number',
  singlePhase: 'Single phase',
  stickLogger: 'Stick logger',
  storageInverter: 'Storage inverter',
  threePhase: 'Three phase',
  title: 'Device details',
  updateError: 'Error during update',
  updateTimeout: 'Device did not respond. Check connection and try again.',
  updateSuccess: 'Update started successfully',
  updatingMessage: 'Updating in progress, please wait...',
  upgrade: 'Update',
  inconsistentFw: 'An inconsistency in the firmware versions has been detected. Please continue trying to update the required modules.',
  deviceNotSupported: 'This device firmware is not supported for remote upgrade operations.',
  rateLimited: 'Too many requests. Please try again later.',
  ahStatusDownloading: 'Downloading...',
  ahStatusDownloaded: 'Download complete',
  ahStatusDownloadFailed: 'Download interrupted',
  ahStatusUploading: 'Uploading {progress}%',
  ahStatusUploadError: 'Upload error',
  ahStatusVerified: 'Verification complete',
  ahStatusVerifyError: 'Verification error',
  ahStatusComplete: 'Complete',
  ahSendingUpgrade: 'Sending upgrade command...',
});

export type InvUpgradeAvailable = {
  sn: string;
  gatewayId: string;
  currentVersion: string;
  fw: ChipData;
  latestVersion: string;
  module: 'ARM' | 'DSPM' | 'DSPS' | 'BMS' | 'PCU' | 'BDU' | 'SOFAR';
  isUpgrading?: boolean;
  upgradeStatus?: number | null;    // AH: 0=download, 1=downloaded, 3=uploading, 7=complete
  upgradeProgress?: number | null;  // AH: 0-100 progress percentage (status 3 only)
};

export interface AhModuleProgressState {
  updating: boolean;
  operational: string;
  modules: Record<string, { status: number; progress: number } | null>;
}

export type StickUpgradeAvailable = {
  sn: string;
  currentVersion: string;
  fw: CallPROSolarmanFirmwareDetailsResponse;
  loggerInfo: CallPROSolarmanDeviceDetailsResponse;
  latestVersion: string;
  module: 'LOGGER';
  isUpgrading?: boolean;
};

const DSPM_INCONSISTENT_VERSION = '0V000000'

const DSPS_INCONSISTENT_VERSION = '0V000000'

export type UpgradeAvailable = InvUpgradeAvailable | StickUpgradeAvailable;

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-768': 768,
})
@Component({
  selector: 'lib-upgrade-inverter',
  standalone: true,
  imports: [
    AzzurroTable,
    CommonModule,
    DeviceDetailModule,
    IonicModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TitleContentComponent,
    UpgradeSafetyModule,
    OneClickPanelComponent,
  ],
  templateUrl: './upgrade-inverter.component.html',
  styleUrls: ['./upgrade-inverter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeInverterComponent implements ICustomBlock, IReduxBlock, OnInit, OnDestroy {

  @Input() templateRef: TemplateRef<UpgradeAvailable> | undefined;

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  // Template references for azzurro-table custom columns
  @ViewChild('currentVersionTemplate', { static: true }) currentVersionTemplate!: TemplateRef<any>;
  @ViewChild('latestVersionTemplate', { static: true }) latestVersionTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;

  // Column configuration for azzurro-table (initialized in ngOnInit)
  tableColumns: ColumnConfig[] = [];

  // AH: multi-module selection
  @ViewChild('selectTemplate', { static: true }) selectTemplate!: TemplateRef<any>;
  selectedModules = new Set<string>();

  readonly _inverterId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set inverterSn(value: string | undefined) {
    if (value !== this._inverterId$.value) {
      // Reset state when device changes (not on every refresh to avoid flickering)
      this.stickUpgradeConfig = null;
      this.isDeviceNotSupported = false;
      this.isRateLimited = false;
      this.isSafetyExpanded = false;
      // ONE-CLICK [temporary — see docs/refactoring/ONE-CLICK-UPGRADE-FEATURE.md]
      this.oneClickGate = null;
      this.oneClickCtx = null;
      this.legacyCompletionSub?.unsubscribe();
      this.legacyCompletionSub = null;
      this.clearOptimisticExpiryRefresh();
      this._inverterId$.next(value);
    }
  }

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  @Input() uiFramework: 'material' | 'ionic' = 'material';
  @Input() isAzzurroHub: boolean = false;
  @Input() gatewaySN?: string;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  private destroy$ = new Subject<void>();

  loggerInfo?: {
    firmwareList: CallPROSolarmanFirmwareDetailsResponse[];
    communicationFwDetails: CallPROSolarmanDeviceDetailsResponse;
    lastAction: CallPROSolarmanLastActionResponse | null;
    isLoggerOnline: boolean;
  };
  inverterInfo?: {
    firmwareList: CallPROSolarmanFirmwareDetailsResponse[];
    inverterFwDetails: CallPROSolarmanDeviceDetailsResponse;
    lastAction: CallPROSolarmanLastActionResponse | null; /** null = lastAction non disponibile (safeLastAction) — trattato come "nessun upgrade in corso" */
    isLoggerOnline: boolean;
    isInverterOnline: boolean;
  };
  inverterUpgradeConfig?: InverterUpgradeConfig | null;
  stickUpgradeConfig?: SolarmanStickUpgradeConfig | null;

  isUpdating: boolean = false;
  isSafetyExpanded: boolean = false;
  isRateLimited: boolean = false;
  /** Push-driven completion watcher for legacy Solarman upgrades (stick/modules). */
  private legacyCompletionSub: Subscription | null = null;
  /** Safety net: re-runs the pipeline when the optimistic window expires. */
  private optimisticExpiryTimer: ReturnType<typeof setTimeout> | null = null;

  // ONE-CLICK [temporary — see docs/refactoring/ONE-CLICK-UPGRADE-FEATURE.md]
  oneClickGate: OneClickGate | null = null;
  oneClickCtx: {
    inverterSn: string;
    loggerId: string;
    inverterId: string;
    solarmanDeviceId?: string | number;
    currentVersions: OneClickCurrentVersions;
  } | null = null;
  private _ahDeviceConnected: boolean = false;
  _ahModbusAddress?: number;
  readonly ahChannelType = CommunicationChannelType.InternetAH_ZCS;

  /** AH real-time progress — BehaviorSubject so async pipe always gets latest value */
  readonly moduleProgress$ = new BehaviorSubject<AhModuleProgressState | null>(null);
  private readonly ahUpgradeActive$ = new BehaviorSubject<boolean>(false);

  /**
   * True when device firmware is not supported for remote upgrades.
   * This happens when the firmware version doesn't have a matching config in S3.
   */
  private _isDeviceNotSupported: boolean = false;
  get isDeviceNotSupported(): boolean {
    return this._isDeviceNotSupported;
  }
  set isDeviceNotSupported(value: boolean) {
    if (this._isDeviceNotSupported !== value) {
      this._isDeviceNotSupported = value;
      this.deviceNotSupportedChange.emit(value);
    }
  }

  /**
   * Emits when device support status changes.
   * Used by parent components to conditionally show/hide related UI elements.
   */
  @Output() deviceNotSupportedChange = new EventEmitter<boolean>();

  readonly latestFwVersions$ = combineLatest([
    this._inverterId$,
    // Use refresh$ directly (not _refresh$ from decorator) to avoid
    // spurious re-fetches triggered by metadata$ store emissions
    this.refresh$.pipe(startWith(undefined)),
  ]).pipe(
    debounceTime(150),
    tap(([inverterSn]) => Logger.state(`latestFwVersions$ triggered for ${inverterSn}`)),
    // exhaustMap: ignore new triggers while assessUpgradeChannelStatus is running
    // (Promises are not cancellable, so switchMap would start duplicates)
    exhaustMap(([inverterSn]) => {
      if (!inverterSn) return of(null);
      return this.isAzzurroHub
        ? from(this.assessUpgradeChannelStatusForAH(inverterSn))
        : from(this.assessUpgradeChannelStatus(inverterSn));
    }),
    tap((fwList) => {
      Logger.api(`assessUpgradeChannelStatus result: ${fwList?.length ?? 0} upgrades available`);

      const deviceId = this._inverterId$.value ?? '';

      // AH: no optimistic update needed — real-time subscription provides state
      // ONE-CLICK [temporary]: the one-click panel owns its own progress state
      if (!this.isAzzurroHub && !this.oneClickGate) {
        const optimisticUpdating = localStorage.getItem(GEN_OPTIMISTIC_UPDATE_KEY(deviceId));
        if (optimisticUpdating && new Date().getTime() - parseInt(optimisticUpdating, 10) < OPTIMISTIC_UPGRADE_DELAY) {
          Logger.state('Optimistic update still in progress');
          this.isUpdating = true;
          if (deviceId) {
            this.inverterStatusFacade.setUpgradeInProgress(deviceId, true);
          }
          // Safety net: without it NOTHING re-runs the pipeline after the
          // optimistic window, and the spinner stays on forever (the pipeline
          // only re-runs on manual refresh$ / device change).
          const remainingMs = OPTIMISTIC_UPGRADE_DELAY - (new Date().getTime() - parseInt(optimisticUpdating, 10));
          this.armOptimisticExpiryRefresh(remainingMs);
          return;
        }
      }

      const someFwIsUpgrading = fwList?.some((fw) => fw.isUpgrading) ?? false;
      this.isUpdating = someFwIsUpgrading;
      if (someFwIsUpgrading && !this.isAzzurroHub) {
        // Pull says still upgrading: schedule the next re-check so the spinner
        // can never outlive the update when pushes don't arrive.
        this.armOptimisticExpiryRefresh(OPTIMISTIC_UPGRADE_DELAY);
      }

      // AH: pre-select all available modules by default
      if (this.isAzzurroHub && fwList?.length) {
        this.selectedModules.clear();
        fwList.forEach((fw) => this.selectedModules.add(fw.module));
      }

      // Sync upgrade state with facade for cross-component communication.
      // ONE-CLICK [temporary]: with the gate active the assess is empty by
      // design — the panel's phase transitions drive the facade instead
      // (onOneClickUpgradeInFlight) and must not be stomped by a mid-flash
      // refresh re-running this pipeline.
      if (deviceId && !this.oneClickGate) {
        this.inverterStatusFacade.setUpgradeInProgress(deviceId, someFwIsUpgrading);
      }
    }),
    // Store-first Current Version: keep the assess snapshot aligned with the
    // subscription push values until the next manual refresh/assess.
    switchMap((fwList) => this.overlayStoreVersions$(fwList ?? null)),
    // Ensure undefined is mapped to null for azzurro-table compatibility
    map((fwList) => fwList ?? null),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  readonly hasInconsistentFw$ = this.latestFwVersions$.pipe(
    map((fwList) => {
      if (!fwList) {
        return [];
      }

      const hasInconsistentFw = fwList.filter((fw) => {
        if (fw.module === 'DSPM' && fw.currentVersion === DSPM_INCONSISTENT_VERSION) {
          return true;
        }

        if (fw.module === 'DSPS' && fw.currentVersion === DSPS_INCONSISTENT_VERSION) {
          return true;
        }

        return false;
      });
      return hasInconsistentFw.length > 0;
    }),
  );

  /**
   * Determines if the device is upgradable for safety files.
   * Based on inverterUpgradeConfig populated by assessUpgradeChannelStatus.
   * Device must exist and be of type INVERTER.
   */
  readonly isDeviceUpgradable$ = this.latestFwVersions$.pipe(
    map(() => {
      if (!this.inverterUpgradeConfig) {
        return false;
      }
      return this.inverterUpgradeConfig.DEVICE_MODEL_ID === 'INVERTER';
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  /**
   * True when any device upgrade is in progress.
   * Used to hide safety upgrade section and batch button during firmware updates.
   * For AH: also considers ahUpgradeActive$ (fed by moduleProgress$ in ngOnInit).
   */
  readonly isAnyUpgradeInProgress$ = combineLatest([
    this.latestFwVersions$,
    this.ahUpgradeActive$,
  ]).pipe(
    map(([fwList, ahActive]) =>
      this.isUpdating || fwList?.some((fw) => fw.isUpgrading) || ahActive || false
    ),
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  /**
   * Determines if the device is online (for disabling safety upgrade).
   * Uses inverterInfo populated by assessUpgradeChannelStatus.
   */
  readonly isOnline$ = this.latestFwVersions$.pipe(
    map(() => this.isAzzurroHub ? this._ahDeviceConnected : (this.inverterInfo?.isInverterOnline ?? false)),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  constructor(
    private readonly accessSupportService: AccessSupportService,
    private dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly getCommunicationChannelForStickQuery: GetCommunicationChannelForStickQuery,
    private readonly getLoggerDataQuery: GetLoggerDataQuery,
    private readonly insertDeviceUpdateLogMutation: InsertDeviceUpdateLogMutation,
    private readonly insertLoggerUpdateLogMutation: InsertLoggerUpdateLogMutation,
    private readonly inverterStatusFacade: InverterStatusFacade,
    private readonly popoverController: PopoverController,
    private readonly onReceiveAckSubscription: OnReceiveAckSubscription,
    private readonly sendInvUpgradeByAHForWebMutation: SendInvUpgradeByAHForWebMutation,
    private readonly sendInvUpgradeForWebMutation: SendInvUpgradeForWebMutation,
    private readonly sendInvUpgradeByEthPollingForWebMutation: SendInvUpgradeByEthPollingForWebMutation,
    private readonly toastFacade: ToastFacade,
    private readonly uiPromiseService: UiPromiseService,
    private readonly upgradeService: UpgradeService,
    // ONE-CLICK [temporary — see docs/refactoring/ONE-CLICK-UPGRADE-FEATURE.md]
    private readonly oneClickUpgradeService: OneClickUpgradeService,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
  ) {
    this.assessUpgradeChannelStatus = this.assessUpgradeChannelStatus.bind(this);
    this.sendUpgradeCommand = this.sendUpgradeCommand.bind(this);
    this.logUpgradeDevice = this.logUpgradeDevice.bind(this);
  }

  async logUpgradeDevice(fw: UpgradeAvailable) {
    const insertQuery = gql`
      fragment responsestatustype on ResponseStatusType {
        success
        statusCode
        message
      }
    `;

    const device_sn = this._inverterId$.value;
    const timestamp = Math.floor(Date.now() / 1000);

    Sentry.captureEvent({
      message: `Aggiornamento firmware avviato per il dispositivo ${device_sn}`,
      level: "info",
      tags: {
        operation: "firmware-upgrade",
        device_sn,
        module: fw.module,
      },
      extra: {
        starting_version: fw.currentVersion,
        target_version: fw.latestVersion,
        timestamp,
        gateway_sn: fw.module === LOGGER_MODULE ? device_sn : fw.gatewayId,
        upgrading_module: fw.module,
      },
    });

    if (fw.module === LOGGER_MODULE) {
      this.insertLoggerUpdateLogMutation.execute(insertQuery, {
        input: {
          device_sn,
          starting_version: fw.currentVersion,
          target_version: fw.latestVersion,
          timestamp,
        },
      });
    } else {
      this.insertDeviceUpdateLogMutation.execute(insertQuery, {
        input: {
          device_sn,
          gateway_sn: fw.gatewayId,
          starting_version: fw.currentVersion,
          target_version: fw.latestVersion,
          timestamp,
          upgrading_module: fw.module,
        },
      });
    }
  }

  ngOnInit(): void {
    // Initialize table columns configuration
    const baseColumns: ColumnConfig[] = [
      {
        columnDef: 'module',
        header: this.labels.module,
        type: ColumnTypeEnum.Text,
        cell: (row: UpgradeAvailable) => row.module,
      },
      {
        columnDef: 'currentVersion',
        header: this.labels.currentVersion,
        type: ColumnTypeEnum.Template,
        template: this.currentVersionTemplate,
      },
      {
        columnDef: 'latestVersion',
        header: this.labels.latestVersion,
        type: ColumnTypeEnum.Template,
        template: this.latestVersionTemplate,
      },
    ];

    // AH: checkbox selection column + progress; Solarman: single upgrade button per row
    this.tableColumns = [
      ...baseColumns,
      {
        columnDef: 'actions',
        header: this.isAzzurroHub ? '' : this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.isAzzurroHub ? this.selectTemplate : this.actionsTemplate,
      },
    ];

    // AH: setup real-time module progress from store subscription
    if (this.isAzzurroHub) {
      // Subscribe to store and push progress into BehaviorSubject (async pipe compatible)
      combineLatest([
        this._inverterId$,
        this.store.select(selectedSystemInvertersWithLoggerData),
      ]).pipe(
        map(([id, devices]) => {
          const dev = devices?.find(d => d.id === id);
          if (!dev) return null;
          return {
            updating: dev.ahUpdating ?? false,
            operational: dev.ahOperational ?? 'normal',
            modules: {
              ARM: dev.ahModuleProgress?.arm ?? null,
              DSPM: dev.ahModuleProgress?.dspm ?? null,
              DSPS: dev.ahModuleProgress?.dsps ?? null,
              SOFAR: dev.ahModuleProgress?.sofar ?? null,
            },
          } as AhModuleProgressState;
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntil(this.destroy$),
      ).subscribe(state => {
        this.moduleProgress$.next(state);
        this.ahUpgradeActive$.next(state?.updating ?? false);
        this.changeDetectorRef.markForCheck();
      });

      // When updating transitions from true→false, auto-refresh to update firmware versions
      this.moduleProgress$.pipe(
        pairwise(),
        filter(([prev, curr]) => !!prev?.updating && !curr?.updating),
        takeUntil(this.destroy$),
      ).subscribe(() => {
        Logger.state('AH upgrade completed — auto-refreshing firmware versions');
        this.isUpdating = false;
        this.changeDetectorRef.detectChanges();
        this.refresh$.next();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.legacyCompletionSub?.unsubscribe();
    this.clearOptimisticExpiryRefresh();
  }

  expandSafety(): void {
    this.isSafetyExpanded = true;
    this.changeDetectorRef.markForCheck();
  }

  // AH multi-module selection
  toggleModuleSelection(module: string): void {
    if (this.selectedModules.has(module)) {
      this.selectedModules.delete(module);
    } else {
      this.selectedModules.add(module);
    }
  }

  isModuleSelected(module: string): boolean {
    return this.selectedModules.has(module);
  }

  async sendBatchUpgradeCommand(): Promise<void> {
    const fwList = await this.latestFwVersions$.pipe(first()).toPromise();
    if (!fwList) return;

    const selected = fwList.filter(
      (fw): fw is InvUpgradeAvailable => fw.module !== 'LOGGER' && this.selectedModules.has(fw.module)
    );
    if (selected.length === 0) return;

    const confirmed = await this.openBatchConfirmDialog(selected);
    if (!confirmed) return;

    selected.forEach(fw => this.logUpgradeDevice(fw));

    const deviceId = this._inverterId$.value ?? '';
    this.inverterStatusFacade.setUpgradeInProgress(deviceId, true);
    this.isUpdating = true;
    this.changeDetectorRef.detectChanges();

    try {
      await this.uiPromiseService.run(
        async () => {
          // Download all firmware files in parallel
          const preparations = await Promise.all(
            selected.map(fw => this.prepareFirmwareData(fw))
          );

          const responseQuery = gql`
            fragment sendinvupgradebyahresponse on SendInvUpgradeByAHResponse {
              transactionId inverterId loggerId timestamp
              input { fileType fileCRC fileURL fileSize CC CR }
            }
          `;

          const input = preparations.map((data) => ({
            fileType: `${data.fileType}`,
            fileCRC: data.fileCRC.toString(16),
            fileURL: data.link,
            fileSize: `${data.totalSize}`,
          }));

          const transactionId = generateTransactionId();
          Logger.api(`[${deviceId}] sendBatchUpgrade: ${selected.length} modules, transactionId=${transactionId}, input:`, JSON.stringify(input));

          // Start listening for ACK BEFORE sending the command (avoids race condition)
          const ackPromise = this.waitForAck(this.gatewaySN!, transactionId);

          await this.sendInvUpgradeByAHForWebMutation.execute(responseQuery, {
            loggerId: this.gatewaySN!,
            deviceSN: selected[0].sn,
            deviceType: AHdeviceTypeEnum.inverter,
            transactionId,
            input,
          });
          Logger.api(`[${deviceId}] Mutation sent, waiting for ACK...`);

          // Wait for EMS to confirm it received and will process the upgrade
          const ack = await ackPromise;
          Logger.success(`[${deviceId}] Batch upgrade ACK received: success=${ack.success}, modules=${selected.map(s => s.module).join(', ')}`);
        },
        { loading: true, loadingMessage: this.labels.ahSendingUpgrade },
      );

      this.selectedModules.clear();
      this.toastFacade.present(this.labels.updateSuccess, '', { severity: 'success', browserMode: 'simple' });
      this.refresh$.next();
    } catch (error: any) {
      const msg = error?.message?.includes('timeout') ? this.labels.updateTimeout : this.labels.updateError;
      this.toastFacade.present(msg, '', { severity: 'error', browserMode: 'simple' });
      this.inverterStatusFacade.setUpgradeInProgress(deviceId, false);
      this.isUpdating = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  getUpgradeStatusLabel(status: number | null | undefined, progress: number | null | undefined): string {
    switch (status) {
      case 0: return this.labels.ahStatusDownloading;
      case 1: return this.labels.ahStatusDownloaded;
      case 2: return this.labels.ahStatusDownloadFailed;
      case 3: return this.labels.ahStatusUploading.replace('{progress}', `${progress ?? 0}`);
      case 4: return this.labels.ahStatusUploadError;
      case 5: return this.labels.ahStatusVerified;
      case 6: return this.labels.ahStatusVerifyError;
      case 7: return this.labels.ahStatusComplete;
      default: return '';
    }
  }

  async assessUpgradeChannelStatus(inverterId: string) {
    Logger.api(`assessUpgradeChannelStatus START for ${inverterId}`);

    // Note: isDeviceNotSupported is reset in the inverterSn setter when device changes,
    // NOT here on every refresh to avoid flickering
    // Rate limit is transient — reset on every attempt so a retry can succeed
    this.isRateLimited = false;

    try {
      // Fetch communication info once and share it between logger and inverter calls
      // This avoids a duplicate deviceInfoRequest (was called twice before)
      const communication = await this.upgradeService.getCommunicationInfoFromInverterSerial(`${inverterId}`);

      const skipFirmwareList = { skipFirmwareList: true };
      const [loggerFwList, inverterFwList, fwUpgradeConfig] = await Promise.all([
        this.upgradeService.getLoggerFwListByInverterSerial(`${inverterId}`, undefined, communication, skipFirmwareList),
        this.upgradeService.getInverterFwListByInverterSerial(`${inverterId}`, undefined, communication, skipFirmwareList),
        this.accessSupportService.getDeviceModuleInfo(inverterId),
      ]);

      Logger.api(`[${inverterId}] Solarman+S3 parallel calls done: loggerFwList=${!!loggerFwList}, inverterFwList=${!!inverterFwList}, fwUpgradeConfig=${!!fwUpgradeConfig}`);
      console.log(`[UpgradeInverter] [${inverterId}] S3 fwUpgradeConfig response:`, JSON.stringify(fwUpgradeConfig));

      this.loggerInfo = loggerFwList;
      this.inverterInfo = inverterFwList;
      this.inverterUpgradeConfig = fwUpgradeConfig as InverterUpgradeConfig;
    } catch (error) {
      if (error instanceof SolarmanRateLimitError) {
        Logger.warn(`[${inverterId}] Rate limited by Solarman API`);
        this.isRateLimited = true;
      } else {
        Logger.error('Initial API failed ' + JSON.stringify({ msg: (error as Error)?.message, name: (error as Error)?.name }));
        this.isDeviceNotSupported = true;
      }
      return [];
    }

    const fwVersion = extractLoggerVersion(this.loggerInfo);
    const loggerSn = extractLoggerSn(this.loggerInfo);
    const deviceSn = extractDeviceSn(this.loggerInfo);

    Logger.api(`[${inverterId}] Logger fw=${fwVersion}, loggerSn=${loggerSn}, deviceSn=${deviceSn}`);

    if (!fwVersion || !loggerSn) {
      Logger.warn('EXIT: No logger firmware version or loggerSn found');
      this.isDeviceNotSupported = true;
      return [];
    }

    this.stickUpgradeConfig ??= (await this.accessSupportService.getDeviceModuleInfo(fwVersion)) as SolarmanStickUpgradeConfig;
    console.log(`[UpgradeInverter] [${inverterId}] S3 stickUpgradeConfig response:`, JSON.stringify(this.stickUpgradeConfig));
    Logger.api(`[${inverterId}] S3 stick config: hasConfig=${!!this.stickUpgradeConfig}, latestLogger=${this.stickUpgradeConfig?.LATEST_SOLARMAN_LOGGER_BY_WEB}`);

    if (!this.stickUpgradeConfig?.LATEST_SOLARMAN_LOGGER_BY_WEB) {
      Logger.warn('EXIT: No LATEST_SOLARMAN_LOGGER_BY_WEB in config - device not supported');
      this.isDeviceNotSupported = true;
      return [];
    }

    const isLoggerUpgrading = isLoggerUpgrading$(this.loggerInfo.lastAction);
    const fwComparison = compareFirmwareVersions(
      fwVersion.toUpperCase(),
      this.stickUpgradeConfig.LATEST_SOLARMAN_LOGGER_BY_WEB.toUpperCase(),
    )
    // Note: ALLOW_SAME_VERSION does NOT apply to stick logger (handled in isUpgradeAvailable with isLogger: true)
    const isLoggerUpdated = fwComparison === FirmwareComparison.GREATER_THAN || fwComparison === FirmwareComparison.EQUAL;

    Logger.api(`[${inverterId}] Logger: updated=${isLoggerUpdated}, upgrading=${isLoggerUpgrading}, comparison=${FirmwareComparison[fwComparison]}, current=${fwVersion}, latest=${this.stickUpgradeConfig.LATEST_SOLARMAN_LOGGER_BY_WEB}`);

    if (!isLoggerUpdated || isLoggerUpgrading) {
      // Fetch logger firmware list on-demand (skipped during initial load to save API calls)
      const firmwareList = await this.upgradeService.getProductFirmwareList(this.loggerInfo.communicationFwDetails.productId);
      const availableUpgrade = firmwareList.find((x) => x.version === this.stickUpgradeConfig?.LATEST_SOLARMAN_LOGGER_BY_WEB);
      if (!availableUpgrade) {
        Logger.warn('EXIT: Logger needs update but firmware not found in list');
        this.isDeviceNotSupported = true;
        return [];
      }

      Logger.success('RETURN: Logger upgrade available');
      return [
        {
          currentVersion: fwVersion,
          fw: availableUpgrade,
          loggerInfo: this.loggerInfo.communicationFwDetails,
          latestVersion: this.stickUpgradeConfig.LATEST_SOLARMAN_LOGGER_BY_WEB,
          module: LOGGER_MODULE,
          isUpgrading: isLoggerUpgrading,
          sn: loggerSn ?? '',
        },
      ];
    }

    const onlyNetwork = true;
    const channels = await this.getCommunicationChannelForStickQuery.execute(
      queryCommunicationChannelForStick,
      {
        loggerId: loggerSn,
        operation: Operation.AGGIORNAMENTO,
      },
      onlyNetwork
    );
    const data = channels.data.getCommunicationChannelForStick;
    console.log(`[UpgradeInverter] [${inverterId}] GraphQL getCommunicationChannelForStick response:`, JSON.stringify(data));
    const { isOnline, communicationChannel } = parseOnlineStatus(data);
    const isAzzurroChannel = communicationChannel !== null;

    Logger.api(`[${inverterId}] GraphQL getCommunicationChannelForStick: channel=${data.CommunicationChannel}, isOnline=${isOnline}, isAzzurro=${isAzzurroChannel}`);

    if (!isAzzurroChannel || !isOnline) {
      Logger.warn(`[${inverterId}] EXIT: Not Azzurro channel or offline, isAzzurro=${isAzzurroChannel}, isOnline=${isOnline}`);
      // Not setting isDeviceNotSupported - device may be temporarily offline
      return [];
    }

    const azzurroMqttChannelState = await this.getLoggerDataQuery.execute(
      queryLoggerData, {
        id: deviceSn,
      },
      onlyNetwork
    );
    console.log(`[UpgradeInverter] [${inverterId}] GraphQL getLoggerData response:`, JSON.stringify(azzurroMqttChannelState?.data?.getLoggerData));

    const inverterProps = ((azzurroMqttChannelState?.data?.getLoggerData) as SolarmanDataType).devices?.find(
      (x) => x.sn === inverterId,
    );

    const zcsVersions = extractModuleVersionsFromZCS(inverterProps);
    const ZCSFirmwareVersions = {
      ARM: zcsVersions.arm,
      DSPM: zcsVersions.dspm,
      DSPS: zcsVersions.dsps,
    };

    // Check if inverter is upgrading from AppSync (our portal)
    const hasInverterPendingFotaStatus = inverterProps?.fota_status === '1';
    // Check if inverter is upgrading from Solarman portal
    const isInverterUpgradingFromSolarman = isLoggerUpgrading$(this.inverterInfo?.lastAction);
    // Combined check: upgrading from either source
    const isInverterUpgrading = hasInverterPendingFotaStatus || isInverterUpgradingFromSolarman;

    Logger.api(`[${inverterId}] GraphQL getLoggerData: fotaStatus=${hasInverterPendingFotaStatus}, solarmanUpgrading=${isInverterUpgradingFromSolarman}, ARM=${ZCSFirmwareVersions.ARM}, DSPM=${ZCSFirmwareVersions.DSPM}, DSPS=${ZCSFirmwareVersions.DSPS}`);

    const inverterFwVersion = this.inverterInfo.inverterFwDetails.paramCategoryList
      /** serialNumber === 2 -> name - Version Information */
      ?.find((versionInfo) => versionInfo.serialNumber === 2)?.fieldList
      /** serialNumber === 8000 -> ARM - Module Version No */
      /** serialNumber === 2700 -> DSPM - Module Version No */
      /** serialNumber === 2800 -> DSPS - Module Version No */
      /** serialNumber === 400 -> BMS - Module Version No */
      /** serialNumber === 14800 -> PCU - Module Version No */
      /** serialNumber === 15900 -> BDU - Module Version No */
      ?.filter((moduleVersion) => [...MODULES].map((fw) => fw.solarmanSerialNumber).includes(moduleVersion.serialNumber));

    const currentVersions: Record<string, string | undefined> = {
      ARM: (ZCSFirmwareVersions.ARM ?? inverterFwVersion?.find(m => m.serialNumber === 8000)?.value)?.toUpperCase(),
      DSPM: (ZCSFirmwareVersions.DSPM ?? inverterFwVersion?.find(m => m.serialNumber === 2700)?.value)?.toUpperCase(),
      DSPS: (ZCSFirmwareVersions.DSPS ?? inverterFwVersion?.find(m => m.serialNumber === 2800)?.value)?.toUpperCase(),
    };

    // ONE-CLICK [temporary — see docs/refactoring/ONE-CLICK-UPGRADE-FEATURE.md]
    // For one-click families the legacy per-module table must never be shown
    // (updating ARM to the new line breaks the per-module path device-side).
    // Ethernet / non-one-click families resolve to 'unsupported' → legacy flow.
    this.oneClickGate = await this.oneClickUpgradeService.evaluateGate(
      this.inverterUpgradeConfig,
      communicationChannel,
      this.inverterInfo?.inverterFwDetails.deviceId,
    );
    if (this.oneClickGate.state !== 'unsupported') {
      this.oneClickCtx = {
        inverterSn: inverterId,
        loggerId: this.loggerInfo?.communicationFwDetails.deviceSn ?? '',
        inverterId: this.inverterInfo?.inverterFwDetails.deviceSn ?? '',
        solarmanDeviceId: this.inverterInfo?.inverterFwDetails.deviceId,
        currentVersions,
      };
      Logger.state('one-click gate active ' + JSON.stringify(this.oneClickGate));
      return [];
    }
    this.oneClickGate = null;
    this.oneClickCtx = null;

    const availableUpgrades = this.buildAvailableUpgrades(currentVersions, inverterId, loggerSn ?? '', isInverterUpgrading);

    /**
     * @Important For now, the BMS, PCU and BDU modules are not included until further checks are made
     */

    Logger.success(`[${inverterId}] DONE: ${availableUpgrades.length} upgrades available: ${availableUpgrades.map((u) => `${u.module}(${u.currentVersion}->${u.latestVersion})`).join(', ') || 'none'}`);
    return availableUpgrades;
  }

  // ONE-CLICK [temporary — see docs/refactoring/ONE-CLICK-UPGRADE-FEATURE.md]
  // The panel owns its progress state but has no facade access (the one-click
  // folder must stay droppable): mirror its in-flight transitions so
  // canConfigureDevice() keeps blocking config while the device is flashing.
  onOneClickUpgradeInFlight(inFlight: boolean): void {
    const deviceId = this._inverterId$.value;
    if (deviceId) {
      this.inverterStatusFacade.setUpgradeInProgress(deviceId, inFlight);
    }
  }

  private buildAvailableUpgrades(
    currentVersions: Record<string, string | undefined>,
    inverterId: string,
    gatewayId: string,
    isUpdating: boolean,
  ): InvUpgradeAvailable[] {
    const upgrades: InvUpgradeAvailable[] = [];

    BASE_MODULES_CONFIG.forEach((moduleConfig) => {
      const currentVersion = currentVersions[moduleConfig.module];
      if (!currentVersion) {
        return;
      }

      const matchingFirmware = this.inverterUpgradeConfig?.[moduleConfig.module]?.filter(
        (fw) => fwVersionLetter(fw.LATEST_VERSION) === fwVersionLetter(currentVersion)
      ) ?? [];

      matchingFirmware.forEach((fw) => {
        if (isUpgradeAvailable(currentVersion, fw.LATEST_VERSION, { isLogger: false })) {
          upgrades.push({
            gatewayId,
            sn: inverterId,
            currentVersion,
            fw,
            latestVersion: fw.LATEST_VERSION,
            module: moduleConfig.module,
            isUpgrading: isUpdating,
          });
        }
      });
    });

    return upgrades;
  }

  async sendUpgradeCommand(fw: UpgradeAvailable) {
    if (this.isUpdating || fw.isUpgrading) {
      this.toastFacade.present(
        this.labels.updatingMessage,
        '',
        {
          severity: 'success',
          browserMode: 'simple',
        }
      );

      return;
    }

    const confirmed = await this.openConfirmDialog(fw);
    if (!confirmed) {
      return;
    }

    this.logUpgradeDevice(fw);

    const deviceId = this._inverterId$.value ?? '';

    this.isUpdating = true;
    this.changeDetectorRef.detectChanges();

    // Notify facade that upgrade is in progress (blocks configuration in other components)
    this.inverterStatusFacade.setUpgradeInProgress(deviceId, true);

    // Only Solarman needs optimistic localStorage update — AH has real-time subscription
    if (!this.isAzzurroHub) {
      localStorage.setItem(GEN_OPTIMISTIC_UPDATE_KEY(deviceId), new Date().getTime().toString());
    }

    try {
      if (this.isAzzurroHub) {
        await this.uiPromiseService.run(
          () => this.sendInvUpgradeByAHCommand(fw as InvUpgradeAvailable),
          { loading: true, loadingMessage: this.labels.ahSendingUpgrade },
        );
      } else if (fw.module === LOGGER_MODULE) {
        await this.sendLoggerUpgradeCommand(fw as StickUpgradeAvailable);
      } else {
        await this.sendInvUpgradeCommand(fw as InvUpgradeAvailable);
      }

      // Legacy Solarman: the send is fire-and-forget, so arm the push-driven
      // completion watcher (AH has its own subscription-based auto-clear).
      if (!this.isAzzurroHub) {
        this.watchLegacyCompletion(fw);
      }

      this.toastFacade.present(this.labels.updateSuccess, '', {
        severity: 'success',
        browserMode: 'simple',
      });
    } catch (error: any) {
      const msg = error?.message?.includes('timeout') ? this.labels.updateTimeout : this.labels.updateError;
      this.toastFacade.present(msg, '', {
        severity: 'error',
        browserMode: 'simple',
      });

      // On error, clear the upgrade state so user can try again
      this.inverterStatusFacade.setUpgradeInProgress(deviceId, false);
      this.isUpdating = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async openConfirmDialog(fw: UpgradeAvailable): Promise<boolean> {
    const content = this.labels.confirmMessage
      .replace('{module}', fw.module)
      .replace('{currentVersion}', fw.currentVersion)
      .replace('{latestVersion}', fw.latestVersion);
    const title = this.labels.confirmTitle;
    if (this.uiFramework === 'material') {
      const dialogRef = this.dialog.open(DialogComponent, {
        width: '600px',
      });
      dialogRef.componentInstance.name = title;
      dialogRef.componentInstance.description = content;
      dialogRef.componentInstance.primary = {
        name: this.labels.confirm,
        click: () => dialogRef.close(true),
      };
      dialogRef.componentInstance.secondary = {
        name: this.labels.cancel,
        click: () => dialogRef.close(false),
      };
      return dialogRef.afterClosed().pipe(first()).toPromise();
    } else {
      const popover = await this.popoverController.create({
        component: DialogComponent,
        componentProps: {
          name: title,
          description: content,
          primary: { name: this.labels.confirm, click: () => popover.dismiss(true) },
          secondary: { name: this.labels.cancel, click: () => popover.dismiss(false) },
        },
        cssClass: 'confirm-popover',
        backdropDismiss: false,
      });
      await popover.present();
      const { data } = await popover.onDidDismiss();
      return data === true;
    }
  }

  private async openBatchConfirmDialog(modules: InvUpgradeAvailable[]): Promise<boolean> {
    const moduleList = modules
      .map(m => `• ${m.module}: ${m.currentVersion} → ${m.latestVersion}`)
      .join('\n');
    const content = this.labels.confirmBatchMessage.replace('{moduleList}', moduleList);
    const title = this.labels.confirmTitle;

    if (this.uiFramework === 'material') {
      const dialogRef = this.dialog.open(DialogComponent, { width: '600px' });
      dialogRef.componentInstance.name = title;
      dialogRef.componentInstance.description = content;
      dialogRef.componentInstance.primary = { name: this.labels.confirm, click: () => dialogRef.close(true) };
      dialogRef.componentInstance.secondary = { name: this.labels.cancel, click: () => dialogRef.close(false) };
      return dialogRef.afterClosed().pipe(first()).toPromise();
    } else {
      const popover = await this.popoverController.create({
        component: DialogComponent,
        componentProps: {
          name: title,
          description: content,
          primary: { name: this.labels.confirm, click: () => popover.dismiss(true) },
          secondary: { name: this.labels.cancel, click: () => popover.dismiss(false) },
        },
        cssClass: 'confirm-popover',
        backdropDismiss: false,
      });
      await popover.present();
      const { data } = await popover.onDidDismiss();
      return data === true;
    }
  }

  async sendLoggerUpgradeCommand(fw: StickUpgradeAvailable) {
    const UPGRADE_TIMEOUT = 600;

    await this.upgradeService.upgradeLoggerFw(fw.loggerInfo, fw.fw, UPGRADE_TIMEOUT);

    this.refresh$.next();
  }

  /**
   * Store-first Current Version column: overlay the subscription-fed values
   * (loggers[].inverter[] — the completion push already carries the new module
   * versions) on the one-shot assess snapshot, which can lag behind it
   * (getLoggerData reads the devices[] twin). The store value wins whenever
   * present; the snapshot stays as fallback for the seed gap (store without
   * inverter[]). Overlay only — the full Solarman assess still runs on manual
   * refresh$ alone (rate limiter).
   */
  private overlayStoreVersions$(fwList: UpgradeAvailable[] | null): Observable<UpgradeAvailable[] | null> {
    const loggerSn = this.loggerInfo?.communicationFwDetails.deviceSn ?? '';
    const inverterSn = this._inverterId$.value ?? '';
    if (this.isAzzurroHub || !fwList?.length || !loggerSn || !inverterSn) {
      return of(fwList);
    }
    const versionsKey = (s: SolarmanUpgradePushState | null): string =>
      s ? `${s.loggerFwVer}|${s.moduleVersions?.ARM}|${s.moduleVersions?.DSPM}|${s.moduleVersions?.DSPS}` : '';
    return this.store.select(selectSolarmanUpgradePushState(loggerSn, inverterSn)).pipe(
      // the selector re-emits on every store mutation: pass only version changes
      distinctUntilChanged((a, b) => versionsKey(a) === versionsKey(b)),
      map((push) => {
        if (!push) {
          return fwList;
        }
        return fwList.map((fw) => {
          const storeVersion = (fw.module === LOGGER_MODULE
            ? push.loggerFwVer
            : push.moduleVersions?.[fw.module as 'ARM' | 'DSPM' | 'DSPS'])?.toUpperCase();
          if (!storeVersion || storeVersion === fw.currentVersion) {
            return fw;
          }
          Logger.state(`store version overlay: ${fw.module} ${fw.currentVersion} -> ${storeVersion}`);
          return { ...fw, currentVersion: storeVersion };
        });
      }),
    );
  }

  /**
   * Legacy Solarman flows (stick logger + single modules) have NO completion
   * channel of their own: the send is fire-and-forget and the spinner used to
   * stay alive on the optimistic flag alone, which nothing ever re-evaluated.
   * Completion is observed on the push side (onPlantStatusUpdate → store):
   *  - stick:   logger `info.fw.firmwareVer` reaching the target version
   *  - modules: `inverter[]` fota_status 1→0 (after being seen active) or the
   *             module version reaching the target
   * Reads inverter[] (NOT the deprecated devices[]). When pushes don't arrive
   * (seed gap, operators without subscription), the optimistic-expiry safety
   * net still closes the spinner via a forced refresh.
   */
  private watchLegacyCompletion(fw: UpgradeAvailable): void {
    const deviceId = this._inverterId$.value ?? '';
    const loggerSn = this.loggerInfo?.communicationFwDetails.deviceSn ?? '';
    const targetVersion = fw.latestVersion?.toUpperCase();

    this.armOptimisticExpiryRefresh(OPTIMISTIC_UPGRADE_DELAY);

    if (!loggerSn || !targetVersion) {
      Logger.warn('watchLegacyCompletion: missing loggerSn/targetVersion — safety net only');
      return;
    }

    let sawFotaActive = false;
    this.legacyCompletionSub?.unsubscribe();
    this.legacyCompletionSub = this.store.select(selectSolarmanUpgradePushState(loggerSn, deviceId))
      .pipe(takeUntil(this.destroy$))
      .subscribe((pushState) => {
        if (!pushState) {
          return;
        }

        if (fw.module === LOGGER_MODULE) {
          if (pushState.loggerFwVer?.toUpperCase() === targetVersion) {
            Logger.state(`legacy completion (stick): info.fw.firmwareVer=${pushState.loggerFwVer}`);
            this.finishLegacyUpgrade(deviceId);
          }
          return;
        }

        if (pushState.fotaStatus === '1' || pushState.updating) {
          sawFotaActive = true;
        }
        const moduleVersion = pushState.moduleVersions?.[fw.module as 'ARM' | 'DSPM' | 'DSPS']?.toUpperCase();
        // normalized compare: the store carries the chip prefix ('0V050004')
        // while the config target does not ('V050004') — raw === never matches
        const versionReached = !!moduleVersion &&
          compareInverterFWModuleVersions(moduleVersion, targetVersion) === FirmwareComparison.EQUAL;
        const fotaCompleted = sawFotaActive && pushState.fotaStatus === '0' && !pushState.updating;
        if (versionReached || fotaCompleted) {
          Logger.state('legacy completion (module) ' + JSON.stringify({
            module: fw.module, moduleVersion, fotaStatus: pushState.fotaStatus, versionReached, fotaCompleted,
          }));
          this.finishLegacyUpgrade(deviceId);
        }
      });
  }

  private finishLegacyUpgrade(deviceId: string): void {
    this.legacyCompletionSub?.unsubscribe();
    this.legacyCompletionSub = null;
    this.clearOptimisticExpiryRefresh();
    localStorage.removeItem(GEN_OPTIMISTIC_UPDATE_KEY(deviceId));
    this.isUpdating = false;
    this.inverterStatusFacade.setUpgradeInProgress(deviceId, false);
    this.changeDetectorRef.detectChanges();
    this.refresh$.next();
  }

  /** Last line of defence: when the optimistic window closes, force one pipeline run so isUpdating is re-evaluated from pull data. */
  private armOptimisticExpiryRefresh(delayMs: number): void {
    this.clearOptimisticExpiryRefresh();
    this.optimisticExpiryTimer = setTimeout(() => {
      Logger.state('optimistic window expired — forcing refresh to re-evaluate isUpdating');
      this.refresh$.next();
    }, Math.max(delayMs, 0) + 5_000);
  }

  private clearOptimisticExpiryRefresh(): void {
    if (this.optimisticExpiryTimer != null) {
      clearTimeout(this.optimisticExpiryTimer);
      this.optimisticExpiryTimer = null;
    }
  }

  /**
   * AH-specific: assess upgrade status using store data + S3 config (no Solarman).
   */
  async assessUpgradeChannelStatusForAH(inverterId: string): Promise<UpgradeAvailable[]> {
    Logger.api(`assessUpgradeChannelStatusForAH START for ${inverterId}`);
    this.isRateLimited = false;

    let fwUpgradeConfig: InverterUpgradeConfig | null = null;
    try {
      fwUpgradeConfig = (await this.accessSupportService.getDeviceModuleInfo(inverterId)) as InverterUpgradeConfig;
    } catch {
      Logger.error(`[${inverterId}] S3 config fetch failed`);
      this.isDeviceNotSupported = true;
      return [];
    }

    if (!fwUpgradeConfig || fwUpgradeConfig.DEVICE_MODEL_ID !== 'INVERTER') {
      Logger.warn(`[${inverterId}] EXIT: device not an inverter or no S3 config`);
      this.isDeviceNotSupported = true;
      return [];
    }

    this.inverterUpgradeConfig = fwUpgradeConfig;
    Logger.api(`[${inverterId}] S3 config: ID=${fwUpgradeConfig.ID}, MIN_SGP_DSPM=${fwUpgradeConfig.MIN_SGP_DSPM}, MIN_SGP_DSPS=${fwUpgradeConfig.MIN_SGP_DSPS}, MIN_SGP_ARM=${fwUpgradeConfig.MIN_SGP_ARM}, SOFAR=${fwUpgradeConfig.SOFAR?.length ?? 0} entries`);

    // Check MIN_SGP — device must support SGP advanced (upgrade via AH)
    // At least one MIN_SGP field must be present to indicate AH upgrade support
    const hasSgpSupport = fwUpgradeConfig.MIN_SGP_DSPM || fwUpgradeConfig.MIN_SGP_DSPS || fwUpgradeConfig.MIN_SGP_ARM;
    if (!hasSgpSupport) {
      Logger.warn(`[${inverterId}] EXIT: no MIN_SGP fields — device family does not support AH upgrade`);
      this.isDeviceNotSupported = true;
      return [];
    }

    // Get current firmware versions from store
    const storeDevices = await this.store.select(selectedSystemInvertersWithLoggerData)
      .pipe(first()).toPromise();
    const device = storeDevices?.find(d => d.id === inverterId);
    if (!device) {
      Logger.warn(`[${inverterId}] EXIT: device not found in store`);
      this.isDeviceNotSupported = true;
      return [];
    }

    this._ahDeviceConnected = device.connectionStatus === 'connected';
    this._ahModbusAddress = device.modbusAdr;
    Logger.api(`[${inverterId}] Store device: connected=${this._ahDeviceConnected}, updating=${device.ahUpdating}, ARM=${device.arm}, DSPM=${device.dspm}, DSPS=${device.dsps}, gateway=${device.loggerId}, modbusAdr=${device.modbusAdr}`);

    // Check each module against its minimum SGP version
    const sgpChecks: Array<{ module: string; current: string | undefined; min: string | undefined }> = [
      { module: 'DSPM', current: device.dspm?.toUpperCase(), min: fwUpgradeConfig.MIN_SGP_DSPM },
      { module: 'DSPS', current: device.dsps?.toUpperCase(), min: fwUpgradeConfig.MIN_SGP_DSPS },
      { module: 'ARM',  current: device.arm?.toUpperCase(),  min: fwUpgradeConfig.MIN_SGP_ARM },
    ];

    for (const check of sgpChecks) {
      if (!check.current || !check.min) continue;

      const comparison = compareInverterFWModuleVersions(check.current, check.min);
      // GREATER_THAN or EQUAL = meets minimum, LESS_THAN = too old, NOT_COMPARABLE = chip mismatch
      const meetsMinimum = comparison === FirmwareComparison.GREATER_THAN || comparison === FirmwareComparison.EQUAL;
      Logger.api(`[${inverterId}] MIN_SGP check: ${check.module}=${check.current}, min=${check.min}, comparison=${FirmwareComparison[comparison]}, meetsMinimum=${meetsMinimum}`);
      if (comparison === FirmwareComparison.NOT_COMPARABLE) {
        Logger.warn(`[${inverterId}] EXIT: ${check.module} chip mismatch — current='${check.current}' vs min='${check.min}'`);
        this.isDeviceNotSupported = true;
        return [];
      }
      if (!meetsMinimum) {
        Logger.warn(`[${inverterId}] EXIT: ${check.module} ${check.current} < MIN_SGP ${check.min}`);
        this.isDeviceNotSupported = true;
        return [];
      }
    }

    const isInverterUpdating = device.ahUpdating ?? false;

    // Strip leading "0" from AH firmware versions (e.g. "0V120005" → "V120005")
    const stripLeadingZero = (v?: string | null) => v?.replace(/^0+(?=[A-Za-z])/, '').toUpperCase();
    const currentVersions: Record<string, string | undefined> = {
      ARM: stripLeadingZero(device.arm),
      DSPM: stripLeadingZero(device.dspm),
      DSPS: stripLeadingZero(device.dsps),
    };

    const availableUpgrades: UpgradeAvailable[] = this.buildAvailableUpgrades(currentVersions, inverterId, this.gatewaySN ?? '', isInverterUpdating);

    // Check SOFAR package availability (AH-only, bundles ARM+DSPM+DSPS)
    const sofarConfigs = this.inverterUpgradeConfig?.SOFAR as SofarPackageData[] | undefined;
    Logger.api(`[${inverterId}] SOFAR configs: ${sofarConfigs?.length ?? 0} entries`);
    if (sofarConfigs?.length) {
      for (const sofarFw of sofarConfigs) {
        const armNewer = currentVersions.ARM && sofarFw.ARM_VERSION &&
          isUpgradeAvailable(currentVersions.ARM, sofarFw.ARM_VERSION, { isLogger: false });
        const dspmNewer = currentVersions.DSPM && sofarFw.DSPM_VERSION &&
          isUpgradeAvailable(currentVersions.DSPM, sofarFw.DSPM_VERSION, { isLogger: false });
        const dspsNewer = currentVersions.DSPS && sofarFw.DSPS_VERSION &&
          isUpgradeAvailable(currentVersions.DSPS, sofarFw.DSPS_VERSION, { isLogger: false });

        if (armNewer || dspmNewer || dspsNewer) {
          availableUpgrades.push({
            gatewayId: this.gatewaySN ?? '',
            sn: inverterId,
            currentVersion: `${currentVersions.DSPM ?? '-'}/${currentVersions.DSPS ?? '-'}/${currentVersions.ARM ?? '-'}`,
            fw: sofarFw,
            latestVersion: `${sofarFw.DSPM_VERSION ?? '-'}/${sofarFw.DSPS_VERSION ?? '-'}/${sofarFw.ARM_VERSION ?? '-'}`,
            module: 'SOFAR',
            isUpgrading: isInverterUpdating,
          });
        }
      }
    }

    Logger.success(`[${inverterId}] AH DONE: ${availableUpgrades.length} upgrades available: ${availableUpgrades.map((u) => `${u.module}(${u.currentVersion}->${u.latestVersion})`).join(', ') || 'none'}`);
    return availableUpgrades;
  }

  /**
   * AH-specific: send upgrade command via sendInvUpgradeByAHForWeb mutation.
   */
  private async prepareFirmwareData(fw: InvUpgradeAvailable): Promise<{
    fileBuffer: ArrayBuffer;
    totalSize: number;
    fileCRC: number;
    fileType: number;
    link: string;
  }> {
    const link = fw.fw.LINK;
    Logger.api(`[${fw.sn}] prepareFirmwareData: downloading ${fw.module} from ${link}`);
    const fileBuffer = await fetch(link).then((response) => response.arrayBuffer());
    if (!fileBuffer) {
      throw new Error('Error downloading firmware update');
    }
    const fileType = fw.module === 'SOFAR' ? 255 : (BASE_MODULES_CONFIG.find((m) => m.module === fw.module)?.solarmanFileType ?? 0);
    const fileCRC = computeCRC32(fileBuffer);
    Logger.api(`[${fw.sn}] prepareFirmwareData: size=${fileBuffer.byteLength}, CRC=${fileCRC.toString(16)}, fileType=${fileType}`);
    return { fileBuffer, totalSize: fileBuffer.byteLength, fileCRC, fileType, link };
  }

  /**
   * Wait for EMS ACK matching a transactionId (one-shot subscription, same pattern as CmdAzzurroHubService).
   * Confirms the AH received the command and will process it.
   */
  private waitForAck(deviceId: string, transactionId: string): Promise<Partial<ReceiveAckResponse>> {
    const response$ = new Observable<Partial<ReceiveAckResponse>>((observer) => {
      let innerSub: Subscription | null = null;

      const outer$ = this.onReceiveAckSubscription.execute(
        receiveAckResponseQuery,
        { deviceId },
      );
      const outerSub = outer$.pipe(first()).subscribe({
        next: (inner$: any) => {
          innerSub = inner$.subscribe({
            next: (res: { data: { onReceiveAck: Partial<ReceiveAckResponse> } }) => {
              observer.next(res.data.onReceiveAck);
            },
            error: (err: any) => observer.error(err),
            complete: () => observer.complete(),
          });
        },
        error: (err: any) => observer.error(err),
        complete: () => {},
      });

      return () => {
        outerSub.unsubscribe();
        innerSub?.unsubscribe();
      };
    }).pipe(
      filter((ack) =>
        ack?.transactionId === transactionId &&
        ack?.success !== null &&
        ack?.success !== undefined
      ),
      first(),
      timeout(AH_ACK_TIMEOUT_MS),
      switchMap((ack) => {
        if (ack.success === false) {
          return throwError(() => new Error(ack.message || 'AH upgrade command rejected'));
        }
        return of(ack);
      }),
    );

    return response$.toPromise().catch((err) => {
      if (err.name === 'TimeoutError') {
        throw new Error('ACK timeout — EMS did not respond');
      }
      throw err;
    }) as Promise<Partial<ReceiveAckResponse>>;
  }

  async sendInvUpgradeByAHCommand(fw: InvUpgradeAvailable): Promise<void> {
    Logger.api(`[${fw.sn}] sendInvUpgradeByAHCommand: module=${fw.module}, gateway=${this.gatewaySN}, target=${fw.latestVersion}`);
    const { totalSize, fileCRC, fileType, link } = await this.prepareFirmwareData(fw);

    const responseQuery = gql`
      fragment sendinvupgradebyahresponse on SendInvUpgradeByAHResponse {
        transactionId
        inverterId
        loggerId
        timestamp
        input {
          fileType
          fileCRC
          fileURL
          fileSize
          CC
          CR
        }
      }
    `;

    const transactionId = generateTransactionId();
    const mutationInput = {
      loggerId: this.gatewaySN!,
      deviceSN: fw.sn,
      deviceType: AHdeviceTypeEnum.inverter,
      transactionId,
      input: [{
        fileType: `${fileType}`,
        fileCRC: fileCRC.toString(16),
        fileURL: link,
        fileSize: `${totalSize}`,
      }],
    };
    Logger.api(`[${fw.sn}] sendInvUpgradeByAHForWeb mutation input (txId=${transactionId}):`, JSON.stringify(mutationInput));

    // Start listening for ACK BEFORE sending the command (avoids race condition)
    const ackPromise = this.waitForAck(this.gatewaySN!, transactionId);

    await this.sendInvUpgradeByAHForWebMutation.execute(responseQuery, mutationInput);
    Logger.api(`[${fw.sn}] Mutation sent, waiting for ACK...`);

    // Wait for EMS to confirm it received and will process the upgrade
    const ack = await ackPromise;
    Logger.success(`[${fw.sn}] ACK received: success=${ack.success}, message=${ack.message ?? 'none'}`);

    this.refresh$.next();
  }

  async sendInvUpgradeCommand(fw: InvUpgradeAvailable) {
    const { totalSize, fileCRC, fileType, link } = await this.prepareFirmwareData(fw);
    const upgradeInput = {
      loggerId: this.loggerInfo?.communicationFwDetails.deviceSn,
      inverterId: this.inverterInfo?.inverterFwDetails.deviceSn,
      fileType,
      fileSize: `${totalSize}`,
      fileCRC: `${fileCRC}`,
      fileURL: link.replace('https', 'http'),
    };

    const onlyNetwork = true;
    if (!this.loggerInfo?.communicationFwDetails.parDeviceSn) {
      throw new Error('Error something went wrong');
    }
    const channel = (await this.getCommunicationChannelForStickQuery.execute(
      queryCommunicationChannelForStick,
      {
        loggerId: this.loggerInfo.communicationFwDetails.parDeviceSn,
        operation: Operation.AGGIORNAMENTO,
      },
      onlyNetwork
    )).data.getCommunicationChannelForStick;

    if( channel.CommunicationChannel === Channel.Azzurro_MQTT ) {
      await this.sendInvUpgradeForWebMutation.execute(queryInvUpgradeForWeb ,{ input: upgradeInput });
    }
    if( channel.CommunicationChannel === Channel.Azzurro_Ethernet ) {
      await this.sendInvUpgradeByEthPollingForWebMutation.execute(queryInvUpgradeForWeb ,{ input: upgradeInput });
    }

    this.refresh$.next();
  }
}
